import { useState, useMemo } from "react";
import { Loader2, Search } from "lucide-react";
import { useAnalytics } from "@/lib/useAnalytics";
import { SectionTitle } from "@/components/ChartCard";
import { fmtNum, fmtGBP, monthLabel } from "@/lib/format";
import { useFilters } from "@/context/FilterContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MODE_LABELS = { SEA: "Sea", AIR: "Air", COU: "Courier" };

const Shipments = () => {
  const { options } = useFilters();
  const [mode, setMode] = useState("");
  const [origin, setOrigin] = useState("");
  const [q, setQ] = useState("");

  const extra = {};
  if (mode) extra.mode = mode;
  if (origin) extra.origin = origin;

  const { data, isLoading } = useAnalytics("shipments", "/shipments", extra);

  const rows = useMemo(() => {
    const items = data?.items || [];
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter(
      (r) =>
        r.shipper.toLowerCase().includes(s) ||
        r.origin_city.toLowerCase().includes(s) ||
        r.destination_city.toLowerCase().includes(s)
    );
  }, [data, q]);

  return (
    <div>
      <SectionTitle title="Shipment Register" description="Full shipment-level detail from CargoWise, filterable and searchable" />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search supplier, origin, destination…"
            data-testid="shipments-search"
            className="h-9 w-72 rounded-sm border border-input bg-background pl-9 pr-3 text-sm outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <Select value={mode || "all"} onValueChange={(v) => setMode(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[140px]" data-testid="shipments-mode">
            <SelectValue placeholder="All modes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All modes</SelectItem>
            {(options?.modes || []).map((m) => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={origin || "all"} onValueChange={(v) => setOrigin(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[170px]" data-testid="shipments-origin">
            <SelectValue placeholder="All origins" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All origins</SelectItem>
            {(options?.origins || []).map((o) => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="ml-auto text-sm text-muted-foreground">
          {isLoading ? "…" : `${rows.length} of ${data?.total || 0} shipments`}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card card-elevated">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="max-h-[640px] overflow-auto">
            <table className="w-full text-sm" data-testid="shipments-table">
              <thead className="sticky top-0 z-10 bg-secondary">
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Supplier</th>
                  <th className="px-4 py-3 font-medium">Mode</th>
                  <th className="px-4 py-3 font-medium">Origin</th>
                  <th className="px-4 py-3 font-medium">Destination</th>
                  <th className="px-4 py-3 text-right font-medium">TEU</th>
                  <th className="px-4 py-3 text-right font-medium">CBM</th>
                  <th className="px-4 py-3 text-right font-medium">Weight (kg)</th>
                  <th className="px-4 py-3 font-medium">ETD</th>
                  <th className="px-4 py-3 font-medium">ETA</th>
                  <th className="px-4 py-3 text-center font-medium">On-Time</th>
                  <th className="px-4 py-3 text-right font-medium">Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-border/60 transition-colors hover:bg-secondary/50">
                    <td className="px-4 py-2.5 font-medium">{r.shipper}</td>
                    <td className="px-4 py-2.5">{MODE_LABELS[r.mode] || r.mode}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.origin_city}, {r.origin_country}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.destination_city}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.teu || "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmtNum(r.volume_cbm)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmtNum(r.weight_kg, 0)}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.etd.slice(0, 10)}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.eta.slice(0, 10)}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-block h-2 w-2 rounded-full ${r.on_time_delivery ? "bg-emerald-500" : "bg-red-500"}`} title={r.on_time_delivery ? "On time" : "Late"} />
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmtGBP(r.total_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Shipments;
